import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faMagnifyingGlass,
  faEllipsisVertical,
  faSliders,
  faDownload,
  faPlus,
  faPen,
  faToggleOn,
  faToggleOff,
  faPaperPlane,
  faTrash,
  faCheckCircle,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/pro-solid-svg-icons';
import { ListUsersParams, UsersService } from '../../../core/services/users.service';
import { User } from '../../../core/models/user.model';
import { PopupConfirmService } from '../../../shared/services/popup-confirm.service';
import { PopupInfoService } from '../../../shared/services/popup-info.service';

@Component({
  selector: 'app-users-list-page',
  imports: [CommonModule, RouterLink, FontAwesomeModule],
  templateUrl: './users-list-page.html',
  styleUrls: ['./users-list-page.scss'],
})
export class UsersListPage implements OnInit, OnDestroy {
  private readonly usersService = inject(UsersService);
  private readonly popupConfirmService = inject(PopupConfirmService);
  private readonly popupInfoService = inject(PopupInfoService);
  private searchSubject = new Subject<string>();
  searchValue!: string;

  faMagnifyingGlass = faMagnifyingGlass;
  faEllipsisVertical = faEllipsisVertical;
  faSliders = faSliders;
  faDownload = faDownload;
  faPlus = faPlus;
  faPen = faPen;
  faToggleOn = faToggleOn;
  faToggleOff = faToggleOff;
  faPaperPlane = faPaperPlane;
  faTrash = faTrash;
  faCheckCircle = faCheckCircle;
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;

  users: User[] = [];
  total = 0;
  page = 1;
  perPage = 10;
  totalPages = 1;
  isLoading = false;
  isLoadingMore = false;
  error: string | null = null;
  openMenuId: string | null = null;
  menuPosition: { top: number; left: number } | null = null;
  private readonly menuWidth = 160; // min-width del dropdown
  private readonly menuHeight = 190; // altura aproximada del menú (4 opciones)

  // Sort
  sortBy: ListUsersParams['sort_by'] = undefined;
  sortDirection: ListUsersParams['sort_direction'] = undefined;

  ngOnInit(): void {
    this.loadUsers();

    // Configurar debounce para la búsqueda
    this.searchSubject
      .pipe(
        debounceTime(500), // Espera 500ms después del último evento
        distinctUntilChanged() // Solo emite si el valor cambió
      )
      .subscribe((search) => {
        this.page = 1;
        this.searchValue = search;
        this.loadUsers({ search });
      });

    // Cerrar menú al hacer scroll
    const wrapper = document.querySelector('.users-table-wrapper');
    if (wrapper) {
      wrapper.addEventListener('scroll', () => this.closeMenu());
    }
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();

    // Limpiar listener de scroll
    const wrapper = document.querySelector('.users-table-wrapper');
    if (wrapper) {
      wrapper.removeEventListener('scroll', () => this.closeMenu());
    }
  }

  loadUsers(params?: ListUsersParams, append = false): void {
    if (append) this.isLoadingMore = true;
    else this.isLoading = true;
    this.error = null;

    const searchParams: ListUsersParams = {
      page: this.page,
      per_page: this.perPage,
      ...params,
    };

    // Si search es undefined o vacío, no incluirlo en los parámetros para evitar confusiones en el backend
    if (searchParams.search === '' || searchParams.search === undefined) delete searchParams.search;

    if (this.sortBy) searchParams.sort_by = this.sortBy;
    if (this.sortDirection) searchParams.sort_direction = this.sortDirection;

    this.usersService.list(searchParams).subscribe({
      next: (response) => {
        if (append) this.users = [...this.users, ...response.data];
        else this.users = response.data;

        this.total = response.meta.total;
        this.page = response.meta.current_page;
        this.perPage = response.meta.per_page;
        this.totalPages = response.meta.last_page;
        this.isLoading = false;
        this.isLoadingMore = false;

        // Auto-load more if container isn't scrollable yet
        if (!append) {
          setTimeout(() => this.checkAndLoadMore(), 100);
        }
      },
      error: (err) => {
        this.error = 'Error al cargar los usuarios';
        this.isLoading = false;
        this.isLoadingMore = false;
        console.error('Error loading users:', err);
      },
    });
  }

  private checkAndLoadMore(): void {
    const wrapper = document.querySelector('.users-table-wrapper') as HTMLElement;
    if (!wrapper) return;

    // If content doesn't overflow and there are more pages, load next
    if (
      wrapper.scrollHeight <= wrapper.clientHeight &&
      this.page < this.totalPages &&
      !this.isLoadingMore
    ) {
      this.page = this.page + 1;
      if (this.searchValue) {
        this.loadUsers({ search: this.searchValue }, true);
      } else {
        this.loadUsers(undefined, true);
      }
    }
  }

  onScroll(event: Event): void {
    const el = (event.currentTarget || event.target) as HTMLElement;
    const threshold = 20; // px from bottom to trigger
    if (this.isLoadingMore || this.isLoading) return;
    if (this.page >= this.totalPages) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
      this.page = this.page + 1;
      if (this.searchValue) {
        this.loadUsers({ search: this.searchValue }, true);
      } else {
        this.loadUsers(undefined, true);
      }
    }
  }

  onSearch(search: string): void {
    // Emitir el valor al subject, el debounce manejará la llamada
    this.searchSubject.next(search);
  }

  onSort(column: ListUsersParams['sort_by']): void {
    if (this.sortBy === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortDirection = 'asc';
    }
    this.page = 1;
    this.loadUsers({ search: this.searchValue });
  }

  getSortIcon(column: ListUsersParams['sort_by']) {
    if (this.sortBy !== column) return this.faSort;
    return this.sortDirection === 'asc' ? this.faSortUp : this.faSortDown;
  }

  //   onFilterByRole(role: string): void {
  //     this.page = 1;
  //     this.loadUsers({ role });
  //   }

  deleteUser(id: string): void {
    const user = this.users.find((u) => u.id === id);
    const userName = user?.name || 'este usuario';

    this.popupConfirmService.showDeleteConfirm(userName, () => {
      this.usersService.delete(id).subscribe({
        next: () => {
          this.loadUsers();
        },
        error: (err) => {
          this.error = 'Error al eliminar el usuario';
          console.error('Error deleting user:', err);
        },
      });
    });
  }

  toggleActive(id: string): void {
    const user = this.users.find((u) => u.id === id);
    if (!user) return;

    const isActive = user.active; // backend may provide `active` flag

    if (isActive) {
      // confirm deactivation
      this.popupConfirmService.showConfirm({
        title: '¿Desactivar usuario?',
        message: `¿Estás seguro de que deseas desactivar al usuario: "${user.name}"?`,
        confirmText: 'Desactivar',
        cancelText: 'Cancelar',
        type: 'warning',
        onConfirm: () => {
          this.usersService.updateStatus(id, false).subscribe({
            next: () => (user.active = false),
            error: (err) => {
              this.error = 'Error al desactivar el usuario';
              console.error('Error deactivating user:', err);
            },
          });
        },
      });
    } else {
      // activate without confirmation
      this.usersService.updateStatus(id, true).subscribe({
        next: () => {
          this.popupInfoService.showInfo(
            `El usuario "${user.name}" ha sido activado correctamente.`
          );
          user.active = true;
        },
        error: (err) => {
          this.error = 'Error al activar el usuario';
          console.error('Error activating user:', err);
        },
      });
    }
  }

  sendInvitation(id: string): void {
    this.usersService.sendInvitation(id).subscribe({
      next: () => {
        // Mostrar notificación breve en vez de modal de confirmación
        this.popupInfoService.showSuccess('La invitación se ha enviado correctamente.');
      },
      error: (err) => {
        this.error = 'Error al enviar la invitación';
        console.error('Error sending invitation:', err);
      },
    });
  }

  toggleMenu(userId: string, event: Event): void {
    event.stopPropagation();

    if (this.openMenuId === userId) {
      this.closeMenu();
      return;
    }

    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    const opensUpward = rect.bottom + 8 + this.menuHeight > window.innerHeight;

    this.menuPosition = {
      top: opensUpward ? Math.max(8, rect.top - 8 - this.menuHeight) : rect.bottom + 8,
      left: Math.max(8, rect.right - this.menuWidth),
    };
    this.openMenuId = userId;
  }

  closeMenu(): void {
    this.openMenuId = null;
    this.menuPosition = null;
  }
}
